import {
  IconBan,
  IconBell,
  IconCalendarCheck,
  IconCheck,
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconEye,
  IconHourglass,
  IconSend,
  IconShieldExclamation,
  IconUsers,
} from '@tabler/icons-react'
import { differenceInMinutes, format, isToday, isYesterday } from 'date-fns'
import { de, enUS, es, fr, it, ptBR } from 'date-fns/locale'

import type {
  Notification,
  NotificationType,
} from '@/api/v1/platform/notifications'
import { PersonCell } from '@/components/common/person-cell'
import { Avatar } from '@/components/sui/avatar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/sui/tooltip'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { cn } from '@/lib/utils'
import { useLanguageStore } from '@/stores/language'

import locales from './locales'

const dateFnsLocales = {
  enUs: enUS,
  ptBr: ptBR,
  esEs: es,
  frFr: fr,
  deDe: de,
  itIt: it,
} as const

type AppLocale = keyof typeof dateFnsLocales

// Short-form timestamps: the group headers (Today/Yesterday/Earlier) already
// carry the day-scale context, so rows only need the fine-grained part.
function formatNotificationTime(
  dateString: string,
  locale: AppLocale,
  nowLabel: string,
): string {
  const date = new Date(dateString)

  if (isToday(date)) {
    const minutes = differenceInMinutes(Date.now(), date)
    if (minutes < 1) return nowLabel
    if (minutes < 60) return `${minutes} min`
    return `${Math.floor(minutes / 60)} h`
  }

  if (isYesterday(date)) {
    return format(date, 'p', { locale: dateFnsLocales[locale] })
  }

  return format(date, locale === 'enUs' ? 'MM/dd' : 'dd/MM')
}

interface NotificationVisual {
  icon: React.ComponentType<{ className?: string }>
  tint: string
}

const neutralTint = 'bg-muted text-muted-foreground'
const blueTint =
  'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
const greenTint =
  'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
const redTint = 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
const amberTint =
  'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'

const notificationVisuals: Record<NotificationType, NotificationVisual> = {
  InternalInvite: { icon: IconUsers, tint: neutralTint },
  EnvelopePublished: { icon: IconSend, tint: blueTint },
  EnvelopeScheduled: { icon: IconClock, tint: blueTint },
  ScheduledEnvelopePublished: { icon: IconCalendarCheck, tint: blueTint },
  EnvelopeViewed: { icon: IconEye, tint: neutralTint },
  EnvelopeApproved: { icon: IconCircleCheck, tint: greenTint },
  EnvelopeRejected: { icon: IconCircleX, tint: redTint },
  EnvelopeCompleted: { icon: IconCircleCheck, tint: greenTint },
  EnvelopeExpired: { icon: IconHourglass, tint: amberTint },
  EnvelopeCancelled: { icon: IconBan, tint: redTint },
  EnvelopeCancelledBySignerMfaError: {
    icon: IconShieldExclamation,
    tint: redTint,
  },
}

type Translate = ReturnType<typeof useComponentTranslation>['t']

// The strong line is the object the user scans for: the envelope name, or the
// inviter for invites. Type labels are only a fallback for payload-less rows.
function getNotificationAnchor(notification: Notification, t: Translate) {
  if (notification.type === 'InternalInvite') {
    return (
      notification.internalInvite?.invitedByName || t('types.InternalInvite')
    )
  }

  const envelopeName =
    notification.envelopeAction?.envelopeName ??
    notification.signerAction?.envelopeName

  if (envelopeName) return envelopeName
  return notification.type ? t(`types.${notification.type}`) : ''
}

function getNotificationAction(notification: Notification, t: Translate) {
  const type = notification.type
  if (!type) return ''

  if (type === 'InternalInvite') {
    return t('actions.InternalInvite', {
      account:
        notification.internalInvite?.invitedByAccountName ||
        t('actions.unknownAccount'),
    })
  }

  return t(`actions.${type}`)
}

interface NotificationItemProps {
  notification: Notification
  onClick: (notification: Notification) => void
  onMarkAsRead?: (notification: Notification) => void
}

export function NotificationItem({
  notification,
  onClick,
  onMarkAsRead,
}: NotificationItemProps) {
  const { t } = useComponentTranslation(locales)
  const locale = useLanguageStore((state) => state.locale)

  const visual = notification.type
    ? notificationVisuals[notification.type]
    : { icon: IconBell, tint: neutralTint }
  const Icon = visual.icon
  const isInvite = notification.type === 'InternalInvite'
  const isSignerAction =
    notification.type === 'EnvelopeViewed' ||
    notification.type === 'EnvelopeApproved' ||
    notification.type === 'EnvelopeRejected'
  const signerName = notification.signerAction?.signerName
  const time = formatNotificationTime(
    notification.createdAtUtc,
    locale,
    t('time.now'),
  )
  const showsMarkAsRead = Boolean(onMarkAsRead) && !notification.markedAsRead

  return (
    <div
      onClick={() => onClick(notification)}
      className={cn(
        'group/notification hover:bg-accent/50 relative flex cursor-pointer items-start gap-3 px-4 py-2.5 transition-colors',
        !notification.markedAsRead && 'bg-primary/5',
      )}
    >
      <div className="relative shrink-0">
        {isInvite && notification.internalInvite ? (
          <Avatar
            name={notification.internalInvite.invitedByName || undefined}
            src={notification.internalInvite.invitedByAvatarUrl || undefined}
            className="h-9 w-9"
          />
        ) : (
          <div
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-full',
              visual.tint,
            )}
          >
            <Icon className="h-4 w-4" />
          </div>
        )}
        {!notification.markedAsRead && (
          <div className="bg-primary absolute -top-0.5 -left-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white dark:ring-zinc-950" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="truncate text-sm leading-tight font-semibold">
            {getNotificationAnchor(notification, t)}
          </p>
          <span
            className={cn(
              'text-muted-foreground shrink-0 text-[11px]',
              // The hover-revealed mark-as-read button overlays this corner
              showsMarkAsRead &&
                'transition-opacity group-hover/notification:opacity-0',
            )}
          >
            {time}
          </span>
        </div>
        {isSignerAction ? (
          <div className="text-muted-foreground mt-0.5 flex min-w-0 items-center gap-1 text-xs leading-relaxed">
            <span className="shrink-0">
              {t(`actions.${notification.type}Prefix`)}
            </span>
            {signerName ? (
              <PersonCell size="sm" name={signerName} />
            ) : (
              <span className="truncate">{t('actions.someone')}</span>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs leading-relaxed">
            {getNotificationAction(notification, t)}
          </p>
        )}
      </div>

      {showsMarkAsRead && onMarkAsRead && (
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                aria-label={t('markAsRead')}
                onClick={(event) => {
                  event.stopPropagation()
                  onMarkAsRead(notification)
                }}
                // Hidden via opacity (not display) so the tooltip keeps its
                // anchor geometry while animating out — display:none would
                // flash the tooltip at the viewport origin.
                className="border-border bg-background text-muted-foreground hover:text-foreground pointer-events-none absolute top-1/2 right-3 flex size-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md border opacity-0 shadow-xs transition-opacity group-hover/notification:pointer-events-auto group-hover/notification:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100"
              />
            }
          >
            <IconCheck className="size-4" />
          </TooltipTrigger>
          <TooltipContent side="left">{t('markAsRead')}</TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}
